import { useCallback, type ReactNode } from 'react';
import { FolderOpen, History } from 'lucide-react';
import { addBreadcrumb } from '../../crash-dialog/services/breadcrumbService';
import { useConnectionsUIStore } from '../../connections/store/uiStore';
import { useSessionsUIStore, saveSessionImmediately } from '../../sessions';
import { useTabHistoryStore } from '../../tab-history';
import { SettingsDialog } from './SettingsDialog';
import { useSettingsDialogStore } from '../store/settingsDialogStore';
import { HelpFeedbackDialog } from '../../help-feedback';
import { Separator } from '../../../components/ui/separator';
import { ResponsiveToolbar, ToolbarSection } from '../../../components/ui/responsive-toolbar';
import { ToolbarPlainButton } from '../../../components/ui/toolbar-button';
import { IntegratedTitlebar } from './IntegratedTitlebar';
import { FirstConnectCoach } from '../../workspace/first-connect-bridge';
import { useRestrictedFeatureDialog } from '../../browser-mode';
import { ToolbarCommandPaletteTrigger } from '../../command-palette';
import { AiToolbarButton } from '../../ai-toolbar';
import { useToolbarActionContext } from '../hooks/toolbarActionContext';
import { useToolbarGating } from '../hooks/useToolbarGating';
import { useMainToolbarActions } from '../hooks/useMainToolbarActions';
import { buildMainToolbarModel } from '../model/mainToolbarModel';
import { toolbarLayoutFromCustomization } from '../model/toolbarCustomization';
import type { ToolbarItemId } from '../model/toolbarItemTypes';
import { useAppStore } from '../../../store/appStore';
import { ToolbarPrimarySlot } from './ToolbarPrimarySlot';
import { ToolbarMoreMenu } from './ToolbarMoreMenu';
import { MainToolbarDialogs } from './MainToolbarDialogs';

/**
 * The engine predicates moved to `../model/toolbarEngineFilters` in task B11;
 * re-exported here so existing importers keep resolving them from this module.
 */
export {
  supportsNotebooksEngineFilter,
  supportsSchemaOverviewEngineFilter,
  supportsSpatialExplorerEngineFilter,
  supportsOracleDbaEngineFilter,
  supportsMssqlDbaEngineFilter,
} from '../model/toolbarEngineFilters';

interface MainToolbarProps {
  className?: string;
}

/**
 * The main toolbar. Both the primary row and the "More ▾" menu are rendered
 * from one descriptor model (`buildMainToolbarModel`) rather than hand-written
 * JSX, so B14 can reorder/hide items by id and B15 can re-default the order
 * without touching this file. The right-hand cluster is fixed and stays
 * literal.
 */
export function MainToolbar({ className = '' }: MainToolbarProps) {
  const ctx = useToolbarActionContext();
  const {
    gating,
    fallbackConnectionId,
    isOracleConnection,
    explorerSelectedTable,
    validDbSelection,
    selectedConnectionId,
    selectedDatabase,
    connections,
  } = useToolbarGating();

  const restrictedDialog = useRestrictedFeatureDialog();
  const { setShowNewConnectionDialog, setShowConnectionsDialog } = useConnectionsUIStore();
  const { openSessionsDialog } = useSessionsUIStore();
  const { openDialog: openTabHistoryDialog } = useTabHistoryStore();
  const openSettingsToSection = useSettingsDialogStore((s) => s.openToSection);

  const { actions, ai, dialogs } = useMainToolbarActions(ctx, {
    fallbackConnectionId,
    selectedConnectionId,
    selectedDatabase,
    connections,
    restrictedDialog,
  });

  const handleOpenAiSettings = useCallback(() => {
    addBreadcrumb('click', 'toolbar:ai-settings');
    openSettingsToSection('ai-integrations');
  }, [openSettingsToSection]);

  const handleAddNewConnection = () => {
    addBreadcrumb('click', 'toolbar:new-connection');
    setShowConnectionsDialog(false);
    setShowNewConnectionDialog(true);
  };

  // `null` (nobody customized) resolves to the shipped default, so a later
  // default reshuffle reaches everyone who never touched Settings → UI.
  const toolbarCustomization = useAppStore((state) => state.toolbarCustomization);
  const model = buildMainToolbarModel(
    { gating, actions },
    toolbarLayoutFromCustomization(toolbarCustomization),
  );
  const moreTrigger = model.primary.find((item) => item.id === 'more');

  const customSlots: Partial<Record<ToolbarItemId, ReactNode>> = {
    ai: (
      <AiToolbarButton
        onOpenAiSettings={handleOpenAiSettings}
        onNewQueryWithAi={ai.newQueryWithAi}
        onNewPackageExplorerWithAi={ai.packageExplorerWithAi}
        isOracleConnection={isOracleConnection}
      />
    ),
    more: moreTrigger ? (
      <ToolbarMoreMenu
        trigger={moreTrigger}
        groups={model.more}
        onOpen={() => ctx.track('toolbar:more_opened')}
      />
    ) : null,
  };

  return (
    // On macOS/Windows this row IS the window's title bar (CH-02). The drag
    // surface and the traffic-light / caption-button insets live in
    // IntegratedTitlebar; `titlebar-content` is what receives the inset padding
    // (on the toolbar itself, so its bottom border still spans the full width).
    <IntegratedTitlebar>
    <ResponsiveToolbar
      data-testid="main-toolbar"
      // CH-10: the title-bar strip is the app's outermost frame, so it takes
      // --chrome (the furthest step from the content canvas) and closes with
      // the structural --divider seam rather than the control hairline.
      // CH-25: `select-none` here is safe because this bar carries only
      // action controls (Home/New/Explorer/Terminal/Overview/More, etc.) —
      // no connection/database name is ever rendered inside it.
      className={`titlebar-content min-h-10 border-b border-divider bg-chrome select-none ${className}`}
    >
      <ToolbarSection position="left">
        {model.primary.map((item) => (
          <ToolbarPrimarySlot key={item.id} item={item} custom={customSlots[item.id]} />
        ))}
      </ToolbarSection>

      <ToolbarSection position="right">
        <ToolbarCommandPaletteTrigger />

        <ToolbarPlainButton
          onClick={() => {
            addBreadcrumb('click', 'toolbar:tab-history');
            ctx.track('toolbar:tab_history');
            openTabHistoryDialog();
          }}
          icon={<History className="h-4 w-4" />}
          label="History"
          title="Open Tab History"
          collapsible
          data-testid="tab-history-button"
        />

        <ToolbarPlainButton
          onClick={() => {
            addBreadcrumb('click', 'toolbar:sessions');
            ctx.track('toolbar:sessions');
            saveSessionImmediately();
            openSessionsDialog();
          }}
          icon={<FolderOpen className="h-4 w-4" />}
          label="Sessions"
          title="Open Sessions dialog"
          collapsible
          data-testid="sessions-button"
        />

        <Separator orientation="vertical" className="mx-1 h-5" />

        <HelpFeedbackDialog />

        <SettingsDialog onAddNewConnection={handleAddNewConnection} />
      </ToolbarSection>

      <MainToolbarDialogs
        tableDesigner={dialogs.tableDesigner}
        validDbSelection={validDbSelection}
        explorerSelectedTable={explorerSelectedTable}
        restrictedDialog={restrictedDialog}
        dataCompare={dialogs.dataCompare}
        schemaCompare={dialogs.schemaCompare}
      />
    </ResponsiveToolbar>
      {/* First-connect orientation coach-mark (anchored under the New Query button). */}
      <FirstConnectCoach />
    </IntegratedTitlebar>
  );
}
